import { createContext, useContext, useState, ReactNode } from "react";
import MessageModal from "../components/common/MessageModal";

interface ModalOptions {
  type?: "confirm" | "info" | "success" | "error" | "warning";
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
}

interface ModalContextType {
  showModal: (options: ModalOptions) => void;
  closeModal: () => void;
}

const MessageModalContext = createContext<ModalContextType | null>(null);

export const useMessageModal = () => {
  const context = useContext(MessageModalContext);
  if (!context) throw new Error("useMessageModal must be used inside provider");
  return context;
};

export const MessageModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalState, setModalState] = useState<
    ModalOptions & { isOpen: boolean }
  >({
    isOpen: false,
    title: "",
    message: "",
  });

  const showModal = (options: ModalOptions) => {
    setModalState({ ...options, isOpen: true });
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <MessageModalContext.Provider value={{ showModal, closeModal }}>
      {children}

      <MessageModal
        isOpen={modalState.isOpen}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        onConfirm={() => {
          modalState.onConfirm?.();
          closeModal();
        }}
        onCancel={closeModal}
      />
    </MessageModalContext.Provider>
  );
};
